import React from 'react';
import { useSelector } from 'react-redux';

import { Modal, Button, TextArea } from '@makemydeal/dr-ui-bricks-components';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { DealVisibility } from '@makemydeal/dr-shared-bff-types';

import { CharacterCount, ButtonContainer } from './RequestChangesModal.styles';
import { useRequestProposalActions } from '../../hook/useRequestProposalActions';
import { getNotes, getRequestStatus } from '../../store';
import { MAX_COMMENT_LENGTH } from '../../constants';
import { useRequestChangeModal } from '../../hook/useRequestChangeModal';

export const RequestChangesModal: React.FC = () => {
    const { handleNotesChange, handleSendRequest } = useRequestProposalActions();
    const requestStatus = useSelector(getRequestStatus);
    const notes = useSelector(getNotes);
    const { isOpen, close } = useRequestChangeModal();

    const isSubmitting = requestStatus === ApiRequestStatus.Loading;
    if (!isOpen) {
        return null;
    }

    const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        handleNotesChange(e.target.value);
    };

    const handleSubmit = () => {
        const visibilityPayload: DealVisibility = { needsManagerApproval: true, approvedByManager: false, isDraftDeal: false };
        handleSendRequest(visibilityPayload);
        close();
    };

    const handleCancel = () => {
        handleNotesChange('');
        close();
    };

    const remainingCharacters = MAX_COMMENT_LENGTH - notes.slice(0, MAX_COMMENT_LENGTH).length;

    return (
        <Modal title="Request Changes" description="Add important notes or comments about this offer" className="no-overflow">
            <TextArea
                label=""
                name="request-changes-comments"
                value={notes}
                onChange={handleCommentsChange}
                inputId="request-changes-comments"
                maxLength={MAX_COMMENT_LENGTH}
                data-testid="request-changes-comments-input"
                placeholder="Comments"
                rows={5}
            />
            <CharacterCount size="small" weight="regular" data-testid="request-changes-character-count">
                Characters remaining: {remainingCharacters}/{MAX_COMMENT_LENGTH}
            </CharacterCount>
            <ButtonContainer>
                <Button
                    buttonStyle="primary"
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={notes.length === 0 || isSubmitting}
                    data-testid="request-changes-submit-button"
                >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
                <Button
                    buttonStyle="tertiary"
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                    data-testid="request-changes-cancel-button"
                >
                    Cancel
                </Button>
            </ButtonContainer>
        </Modal>
    );
};

export default RequestChangesModal;
